import {TOOL_NAMES} from '../constants/tool-names'
import type {CompletionChunk, HydratedLogEntry, LogEntry, StreamingMessage} from '../types/session'
import {isCompletionChunk} from '../types/session'

/**
 * Marker patterns for subagent sessions
 * Supports both legacy "custom agent" and current "subagent" formats for backward compatibility
 */
const AGENT_START_MARKERS = ['▶️ Begin subagent', '▶️ Begin custom agent']
const AGENT_END_MARKERS = ['⏹️ End subagent', '⏹️ End custom agent']

export type CustomAgentGroupLogEntry = HydratedLogEntry | CustomAgentSessionGroup

export interface CustomAgentSessionGroup {
  agentId: string
  startMessage: StreamingMessage | null
  endMessage: StreamingMessage | null
  logs: CustomAgentGroupLogEntry[]
  isPending: boolean
  /** Description from the originating task tool call, e.g. "Find database migrations" */
  description?: string
  /** Agent type from the originating task tool call, e.g. "explore", "task", "general-purpose" */
  agentType?: string
  /** The prompt/instructions given to the subagent */
  prompt?: string
}

export function isCustomAgentSessionGroup(entry: CustomAgentGroupLogEntry): entry is CustomAgentSessionGroup {
  return typeof entry === 'object' && entry !== null && 'agentId' in entry && 'logs' in entry
}

/**
 * Groups log entries by agentId, creating nested structures for subagent sessions.
 * This does NOT refer to the top-level Session — it groups logs from subagents
 * that run within a parent session.
 *
 * SIMPLIFIED STRATEGY:
 * 1. Pre-scan for originating task tool calls to extract descriptions.
 * 2. Single pass through logs.
 * 3. If a log has an agentId, route it to an active subagent session group.
 * 4. If the group doesn't exist yet, create it and add it to the results immediately.
 * 5. Interleaved logs (A, B, A) are handled automatically by the Map reference.
 *
 * The originating tool call (task("...")) stays in the log stream as a separate entry.
 * The group represents the execution results of that task.
 */
export function groupCustomAgentLogs(logs: HydratedLogEntry[]): CustomAgentGroupLogEntry[] {
  const result: CustomAgentGroupLogEntry[] = []

  // Pre-scan: build maps for descriptions and parent-child relationships.
  const taskDescriptions = buildTaskDescriptionMap(logs)
  const childToParentMap = buildChildToParentMap(logs)

  // Map of agentId -> group reference. A Map is used because multiple subagents
  // can run concurrently, each identified by a unique agentId.
  const activeSubagentSessions = new Map<string, CustomAgentSessionGroup>()

  for (const log of logs) {
    const agentId = getCustomAgentId(log)

    // 1. Standard Log (No Agent ID) - Just push to results
    if (!agentId) {
      result.push(log)
      continue
    }

    // 2. Subagent Log - Check if we have an active subagent session
    let subagentSession = activeSubagentSessions.get(agentId)

    if (!subagentSession) {
      // New Subagent Session Found: Create the group and push reference to result immediately
      const taskInfo = taskDescriptions.get(agentId)
      subagentSession = {
        agentId,
        startMessage: null, // Will be filled if/when we find the marker
        endMessage: null,
        logs: [],
        isPending: true,
        description: taskInfo?.description,
        agentType: taskInfo?.agentType,
        prompt: taskInfo?.prompt,
      }
      activeSubagentSessions.set(agentId, subagentSession)
      result.push(subagentSession)
    }

    // 3. Update the Subagent Session
    // Check for markers to update metadata (but don't add them to logs - they're just for grouping)
    if (isCustomAgentStartMarker(log)) {
      subagentSession.startMessage = log as StreamingMessage
    } else if (isCustomAgentEndMarker(log)) {
      subagentSession.endMessage = log as StreamingMessage
      subagentSession.isPending = false
      // Remove from active subagent sessions - this one is complete
      activeSubagentSessions.delete(agentId)
    } else {
      // Only add non-marker logs to the group's internal array
      subagentSession.logs.push(log)
    }
  }

  // Nest child subagent groups inside their parent group's logs.
  // Must happen before filtering so parent groups that only spawn children
  // have visible content (the nested child group) and aren't filtered out.
  const nested = nestChildGroups(result, childToParentMap)

  // Filter out completed subagent groups that have no visible logs.
  // This happens when a subagent's only content is spawning child subagents —
  // the start/end markers are stored as metadata and the child logs go into
  // their own groups, leaving the parent group empty.
  const filtered = nested.filter(entry => {
    if (isCustomAgentSessionGroup(entry) && !entry.isPending && entry.logs.length === 0) {
      return false
    }
    return true
  })

  return reorderGroupsByOriginatingToolCall(filtered)
}

/**
 * Moves child subagent groups from the top-level result into their parent group's logs.
 * A child subagent is one whose originating task tool call was made from within another
 * subagent. The child group is inserted into the parent's logs after the originating
 * CompletionChunk (the task tool call that spawned it).
 */
function nestChildGroups(
  entries: CustomAgentGroupLogEntry[],
  childToParentMap: Map<string, string>,
): CustomAgentGroupLogEntry[] {
  if (childToParentMap.size === 0) return entries

  // Build a lookup of agentId -> group for all groups in the flat result
  const groupsByAgentId = new Map<string, CustomAgentSessionGroup>()
  for (const entry of entries) {
    if (isCustomAgentSessionGroup(entry)) {
      groupsByAgentId.set(entry.agentId, entry)
    }
  }

  const childAgentIds = new Set<string>()

  for (const [childAgentId, parentAgentId] of childToParentMap) {
    const childGroup = groupsByAgentId.get(childAgentId)
    const parentGroup = groupsByAgentId.get(parentAgentId)
    if (!childGroup || !parentGroup) continue

    // Find the originating task tool call in the parent's logs and insert after it
    let inserted = false
    for (let i = 0; i < parentGroup.logs.length; i++) {
      const log = parentGroup.logs[i]
      if (!log || !isCompletionChunk(log as HydratedLogEntry)) continue

      const chunk = log as CompletionChunk
      for (const choice of chunk.choices ?? []) {
        for (const toolCall of choice?.delta?.tool_calls ?? []) {
          if (toolCall?.id === childAgentId) {
            parentGroup.logs.splice(i + 1, 0, childGroup)
            inserted = true
            break
          }
        }
        if (inserted) break
      }
      if (inserted) break
    }

    // Fallback: append to end of parent's logs if originating call wasn't found
    if (!inserted) {
      parentGroup.logs.push(childGroup)
    }

    childAgentIds.add(childAgentId)
  }

  // Remove nested children from the top-level result
  if (childAgentIds.size === 0) return entries
  return entries.filter(entry => {
    if (isCustomAgentSessionGroup(entry) && childAgentIds.has(entry.agentId)) {
      return false
    }
    return true
  })
}

/**
 * Reorders subagent groups to appear immediately after their originating tool call.
 *
 * Without this, groups appear in log-arrival order (whichever subagent's first log
 * chunk arrives from the server first). This is wrong when a slow subagent (e.g.
 * implementation) is invoked before fast ones (e.g. testing) — the slow one's group
 * would appear last even though it was invoked first.
 *
 * The originating tool call is a non-grouped CompletionChunk whose tool_calls[].id
 * matches a subagent group's agentId.
 *
 * Multi-turn subagents reuse the same toolCallId (agentId) across multiple start/end
 * pairs. Only the FIRST group is moved to after the originating tool call; subsequent
 * groups stay in their natural chronological position so they remain interleaved with
 * parent-level messages between turns.
 */
function reorderGroupsByOriginatingToolCall(entries: CustomAgentGroupLogEntry[]): CustomAgentGroupLogEntry[] {
  // For each agentId, only the first group needs to be moved next to the originating
  // tool call. Subsequent groups (from multi-turn subagents) stay in place.
  const firstGroupByAgentId = new Map<string, CustomAgentSessionGroup>()
  for (const entry of entries) {
    if (isCustomAgentSessionGroup(entry) && !firstGroupByAgentId.has(entry.agentId)) {
      firstGroupByAgentId.set(entry.agentId, entry)
    }
  }

  if (firstGroupByAgentId.size === 0) return entries

  // Track which first-groups have been placed after their originating tool call
  const placedAgentIds = new Set<string>()
  const reordered: CustomAgentGroupLogEntry[] = []

  for (const entry of entries) {
    if (isCustomAgentSessionGroup(entry)) {
      // First groups are always placed by originating-tool-call matching (or the
      // orphan fallback below), so skip them here to avoid duplicates.
      if (entry === firstGroupByAgentId.get(entry.agentId)) {
        continue
      }

      // Subsequent groups stay in their natural chronological position
      reordered.push(entry)
      continue
    }

    reordered.push(entry)

    // Check if this entry is an originating tool call that spawned subagent(s)
    if (!isCompletionChunk(entry)) continue

    // A single CompletionChunk can contain multiple tool_calls (parallel subagent invocations).
    // Collect all matching groups in tool_calls array order to preserve invocation order.
    for (const choice of entry.choices) {
      const toolCalls = choice.delta?.tool_calls ?? []
      for (const toolCall of toolCalls) {
        const toolCallId = toolCall?.id
        if (toolCallId && !placedAgentIds.has(toolCallId) && firstGroupByAgentId.has(toolCallId)) {
          const group = firstGroupByAgentId.get(toolCallId)
          if (group) {
            reordered.push(group)
            placedAgentIds.add(toolCallId)
          }
        }
      }
    }
  }

  // Append unmatched first-groups, skipping empty ones that would otherwise float at the bottom.
  for (const [agentId, group] of firstGroupByAgentId) {
    if (!placedAgentIds.has(agentId) && group.logs.length > 0) {
      reordered.push(group)
    }
  }

  return reordered
}

// --- Helpers ---

/**
 * Extracts the agentId from a log entry if present
 */
export function getCustomAgentId(log: HydratedLogEntry | LogEntry): string | null {
  if (typeof log === 'object' && log !== null && 'agentId' in log) {
    const {agentId} = log as {agentId: unknown}
    return typeof agentId === 'string' ? agentId : null
  }
  return null
}

/**
 * Builds a map of toolCallId -> {agentType, description} from originating task tool calls.
 * An originating task tool call is a CompletionChunk that calls the 'task' tool.
 * The tool call's id becomes the agentId of the resulting subagent group.
 * Scans both top-level and nested (within-subagent) task invocations.
 */
function buildTaskDescriptionMap(
  logs: HydratedLogEntry[],
): Map<string, {agentType?: string; description?: string; prompt?: string}> {
  const map = new Map<string, {agentType?: string; description?: string; prompt?: string}>()

  for (const log of logs) {
    if (!isCompletionChunk(log)) continue

    // After dedup merging, a single chunk can contain multiple choices when
    // the model produces parallel tool calls in one turn. Each choice may
    // hold a different tool_call, so we must iterate over all of them.
    // A single choice can also contain multiple tool_calls.
    const choices = log.choices ?? []
    for (const choice of choices) {
      for (const toolCall of choice?.delta?.tool_calls ?? []) {
        if (!toolCall?.function) continue
        if (toolCall.function.name !== TOOL_NAMES.TASK) continue

        const toolCallId = toolCall.id
        if (!toolCallId) continue

        try {
          const args = JSON.parse(toolCall.function.arguments ?? '')
          const agentType = typeof args.agent_type === 'string' ? args.agent_type : undefined
          // Fall back to `name` when `description` is not provided
          const description =
            (typeof args.description === 'string' ? args.description : undefined) ??
            (typeof args.name === 'string' ? args.name : undefined)
          const prompt = typeof args.prompt === 'string' ? args.prompt : undefined

          if (agentType || description || prompt) {
            map.set(toolCallId, {agentType, description, prompt})
          }
        } catch {
          // Malformed arguments — skip this tool call
        }
      }
    }
  }

  return map
}

/**
 * Builds a map of child agentId -> parent agentId for nested subagents.
 * A nested subagent is one whose originating task tool call was made from within
 * another subagent (the CompletionChunk has an agentId).
 */
function buildChildToParentMap(logs: HydratedLogEntry[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const log of logs) {
    const parentAgentId = getCustomAgentId(log)
    if (!parentAgentId) continue
    if (!isCompletionChunk(log)) continue

    for (const choice of log.choices ?? []) {
      for (const toolCall of choice?.delta?.tool_calls ?? []) {
        if (toolCall?.function?.name === TOOL_NAMES.TASK && toolCall.id) {
          map.set(toolCall.id, parentAgentId)
        }
      }
    }
  }
  return map
}

/**
 * Formats a descriptive title for a subagent session group.
 * Uses the description from the originating task tool call when available,
 * falling back to the agent name from the start marker.
 *
 * Examples:
 *   - "Explore: Find database migrations"
 *   - "Task: Run unit tests"
 *   - "Subagent: joker" (fallback when no description)
 */
export function formatAgentTitle(group: CustomAgentSessionGroup): string {
  if (group.description) {
    const prefix = group.agentType ? capitalizeFirst(group.agentType) : 'Subagent'
    return `${prefix}: ${group.description}`
  }

  // Fallback to existing behavior
  const agentName = extractAgentName(group.startMessage)
  return `Subagent: ${agentName}`
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Extracts the parsed name argument from a run_setup tool call, or null if the log
 * is not a valid run_setup completion chunk with a parseable name.
 */
function getRunSetupName(log: HydratedLogEntry): string | null {
  if (!isCompletionChunk(log)) return null

  const toolCall = log.choices?.[0]?.delta?.tool_calls?.[0]
  if (!toolCall?.function || toolCall.function.name !== TOOL_NAMES.RUN_SETUP) return null

  try {
    const args = JSON.parse(toolCall.function.arguments ?? '')
    return typeof args.name === 'string' ? args.name : null
  } catch {
    return null
  }
}

/**
 * Extracts the details.type from a run_setup tool call's arguments, or null.
 */
function getRunSetupDetailsType(log: HydratedLogEntry): string | null {
  if (!isCompletionChunk(log)) return null

  const toolCall = log.choices?.[0]?.delta?.tool_calls?.[0]
  if (!toolCall?.function || toolCall.function.name !== TOOL_NAMES.RUN_SETUP) return null

  try {
    const args = JSON.parse(toolCall.function.arguments ?? '')
    return typeof args.details?.type === 'string' ? args.details.type : null
  } catch {
    return null
  }
}

/**
 * Checks if a log entry is a subagent session start marker
 */
function isCustomAgentStartMarker(log: HydratedLogEntry): boolean {
  const detailsType = getRunSetupDetailsType(log)
  if (detailsType === 'subagent_start') return true

  const name = getRunSetupName(log)
  return name !== null && AGENT_START_MARKERS.some(marker => name.includes(marker))
}

/**
 * Checks if a log entry is a subagent session end marker
 */
function isCustomAgentEndMarker(log: HydratedLogEntry): boolean {
  const detailsType = getRunSetupDetailsType(log)
  if (detailsType === 'subagent_end') return true

  const name = getRunSetupName(log)
  return name !== null && AGENT_END_MARKERS.some(marker => name.includes(marker))
}

/**
 * Extracts the agent name from the start marker tool call.
 * Shared with CustomAgentSessionGroup component.
 * Supports both legacy "custom agent" and current "subagent" formats.
 */
export function extractAgentName(startMessage: CompletionChunk | StreamingMessage | null): string {
  if (!startMessage) return 'Subagent'

  try {
    const toolCall = startMessage.choices?.[0]?.delta?.tool_calls?.[0]
    if (!toolCall?.function?.arguments) return 'Subagent'

    const args = JSON.parse(toolCall.function.arguments)
    const name = args.name || ''

    // Extract the agent name from patterns like "▶️ Begin subagent: the-joker" or "▶️ Begin custom agent: the-joker"
    // Must start with the emoji to avoid false positives from arbitrary text containing these words
    const match = name.match(/^▶️ Begin (?:subagent|custom agent):\s*(.+)$/i)
    if (match) {
      return match[1].trim()
    }

    return name || 'Subagent'
  } catch {
    return 'Subagent'
  }
}
